import React, { useState, useEffect, useRef, useCallback } from "react";
import { buildingAddressService } from "../../../../services/buildingAddressService";
import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../shadcn/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../shadcn/ui/command";
import { Check, ChevronsUpDown, Loader2, MapPin } from "lucide-react";
import { cn } from "../../../../lib/utils";
import logger from "../../../../utils/logger";


// Module-level cache shared across ALL SearchableBuildingAddress instances
const addressCache = {};
const fetchPromises = {};

const SearchableBuildingAddress = ({
  value,
  onChange,
  onBuildingSelect,
  disabled = false,
  placeholder = "Search building addresses...",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [buildingAddresses, setBuildingAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const observerRef = useRef();

  // Fetch building address by ID when value changes (for edit mode and auto-selection)
  useEffect(() => {
    const fetchBuildingById = async () => {
      if (!value) {
        setSelectedBuilding(null);
        return;
      }

      // ONLY fetch if the current selection doesn't match the prop value
      if (!selectedBuilding || selectedBuilding.id !== value) {
        try {
          let data;
          if (addressCache[value]) {
            data = addressCache[value];
          } else if (fetchPromises[value]) {
            data = await fetchPromises[value];
          } else {
            fetchPromises[value] = buildingAddressService
              .getBuildingAddressById(value)
              .then(res => {
                if (res.status === 200 && res.data) {
                  addressCache[value] = res.data;
                  return res.data;
                }
                return null;
              })
              .catch(error => {
                logger.error("Error fetching building address:", error);
                delete fetchPromises[value]; // clear on failure
                return null;
              });
            data = await fetchPromises[value];
          }

          if (data) {
            setSelectedBuilding(data);
          }
        } catch (error) {
          logger.error("Error in fetchBuildingById:", error);
        }
      }
    };
    fetchBuildingById();
  }, [value, selectedBuilding?.id]);

  // Search building addresses with debouncing and pagination
  const fetchBuildingAddresses = useCallback(async (query, pageNum, append = false) => {
    if (!query || query.trim().length < 2) {
      setBuildingAddresses([]);
      setHasMore(false);
      return;
    }

    if (pageNum === 0) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await buildingAddressService.searchBuildingAddresses({
        buildingName: query,
        city: query,
        country: query,
        digitalAddress: query,
        region: query,
        streetAddressContains: query,
        pageNumber: pageNum,
        pageSize: 15,
        paged: true,
      });

      if (response.status === 200) {
        const data = response.data;
        // Typically paged response has .content or .data
        const newAddresses = data.content || data.data || [];
        const totalPages = data.totalPages || 1;

        setBuildingAddresses(prev => (append ? [...prev, ...newAddresses] : newAddresses));
        setHasMore(pageNum < totalPages - 1);
        setImageErrors(new Set());
      } else {
        setBuildingAddresses(prev => (append ? prev : []));
        setHasMore(false);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        logger.error("Error searching building addresses:", error);
        setBuildingAddresses(prev => (append ? prev : []));
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    if (!open) {
      setBuildingAddresses([]);
      setSearchQuery("");
      setPage(0);
      setHasMore(true);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchBuildingAddresses(searchQuery, 0, false);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, fetchBuildingAddresses, open]);

  // Load more when scrolling
  const lastElementRef = useCallback(
    node => {
      if (loading || isLoadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchBuildingAddresses(searchQuery, nextPage, true);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, isLoadingMore, hasMore, page, searchQuery, fetchBuildingAddresses]
  );

  const handleSelect = building => {
    setSelectedBuilding(building);
    setOpen(false);
    setSearchQuery("");
    if (onChange) {
      onChange(building.id);
    }
    if (onBuildingSelect) {
      onBuildingSelect(building);
    }
  };

  const formatBuildingAddress = building => {
    const parts = [building.buildingName, building.streetAddress, building.city, building.country].filter(Boolean);
    return parts.join(", ");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {selectedBuilding ? (
            <div className="flex flex-col items-start overflow-hidden py-1 min-w-0">
              <span className="font-semibold text-sm truncate w-full text-left">{selectedBuilding.buildingName}</span>
              <span className="text-[10px] leading-tight text-muted-foreground truncate w-full text-left">
                {[selectedBuilding.streetAddress, selectedBuilding.city].filter(Boolean).join(", ")}
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[450px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by building name, address, city, etc..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Searching...</span>
              </div>
            ) : buildingAddresses.length === 0 && searchQuery.trim().length >= 2 ? (
              <CommandEmpty>No building addresses found.</CommandEmpty>
            ) : buildingAddresses.length === 0 ? (
              <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search...
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {buildingAddresses.map((building, index) => {
                  const isLast = index === buildingAddresses.length - 1;
                  return (
                    <CommandItem
                      key={building.id}
                      onSelect={() => handleSelect(building)}
                      className="cursor-pointer"
                      ref={isLast ? lastElementRef : null}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200 relative flex items-center justify-center">
                          {building.images &&
                          building.images.length > 0 &&
                          building.images[0] &&
                          !imageErrors.has(building.id) ? (
                            <img
                              src={building.images[0]}
                              alt={building.buildingName}
                              className="w-full h-full object-cover"
                              onError={() => setImageErrors(prev => new Set(prev).add(building.id))}
                            />
                          ) : (
                            <MapPin className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{building.buildingName}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {building.streetAddress}
                            {building.city && `, ${building.city}`}
                            {building.country && `, ${building.country}`}
                          </div>
                        </div>
                        <Check
                          className={cn(
                            "h-4 w-4 flex-shrink-0",
                            selectedBuilding?.id === building.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </div>
                    </CommandItem>
                  );
                })}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4 border-t border-gray-100">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-xs text-muted-foreground">Loading more...</span>
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableBuildingAddress;
